import React from "react";

import { PRODUCT_NAME } from "utilities/productName";
import { TEAMS_SUPPORT_CHANNEL_URL } from "utilities/supportUrl";

interface Props {
  children: React.ReactNode;
  /**
   * Optional render-prop fallback. When omitted, the default generic
   * "unexpected error" UI below is rendered.
   */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface State {
  error: null | Error;
  errorInfo: null | React.ErrorInfo;
}

/**
 * A basic React error boundary.
 * The last line of defense against unexpected errors.
 * Based on: https://codepen.io/gaearon/pen/wqvxGa
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Catch errors in any components below and re-render with error message
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
    // You can also log error messages to an error reporting service here
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
  };

  render() {
    if (this.state.error && this.state.errorInfo) {
      // Caller-provided fallback wins.
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      // Error path
      return (
        <div style={{ maxWidth: "960px" }} className="ma2 pa2 ma3-ns pa3-ns">
          <h2 className="lh-title f3 f2-ns red mb0">Error</h2>
          <p className="lh-copy f5 measure">
            Sorry, an unexpected error has occured.
          </p>
          <p className="f5 lh-copy">
            For support and feedback, please contact the {PRODUCT_NAME} team on
            our{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={TEAMS_SUPPORT_CHANNEL_URL}
            >
              teams channel
            </a>
            .
          </p>

          <details>
            <summary className="pointer b">Error details</summary>
            <p className="f6 measure code mb0">
              {this.state.error && this.state.error.toString()}
            </p>
            <p
              className="f6 code mt0 pa1"
              style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
            >
              {this.state.errorInfo.componentStack}
            </p>
          </details>
        </div>
      );
    }
    // Normally, just render children
    return this.props.children;
  }
}
